import React from "react";
import { Link } from "react-router-dom";
import Footer from "../../features/Footer/Footer";
import PrintResume from "./PrintResume";
import "./CV.css";

const links = [
  { label: "hello@quentinrobert.com", href: "mailto:hello@quentinrobert.com" },
  { label: "LinkedIn", href: "https://www.linkedin.com/in/quentin-robert-a94311274/" },
  { label: "GitHub", href: "https://github.com/Firfal" },
];

const facts = [
  { label: "Looking for", value: "Internship · Full-time · Freelance" },
  { label: "Availability", value: "Now" },
  { label: "Location", value: "Paris, France" },
  { label: "Languages", value: "French (native) · English (professional)" },
];

const projects = [
  {
    title: "Pullback",
    tag: "Hackathon 3rd place",
    link: "https://getpullback.com",
    text: "Native macOS app built in 48 h at the screenpipe × 42 Entrepreneurs hackathon (3rd place, Track 2). Reads your local screenpipe memory, flags what you left unfinished with verbatim proof, then launches a Claude agent that finishes the task; anything leaving your Mac waits for your OK.",
    stack: ["Swift", "Claude API", "Agent loop", "screenpipe", "MCP"],
  },
  {
    title: "OpenFlow",
    tag: "Open-source · MCP",
    link: "https://github.com/Firfal/openflow",
    text: "Next.js sites generated with Claude Code that their owners then edit themselves, Webflow-style. Each site exposes its own MCP server, so owners can edit it by chatting with Claude, ChatGPT or Cursor. AI-written code is checked against a verifiable standard, and Claude Code gets automatic feedback to fix every deviation on its own.",
    stack: ["TypeScript", "Next.js", "MCP", "Claude Code", "Firebase"],
  },
  {
    title: "NestAI Agent",
    tag: "Hackathon winner",
    link: "https://www.linkedin.com/in/quentin-robert-a94311274/",
    text: "Real-estate agent built in 48 h at {Tech: Europe} Paris AI Hack, winner of the Dify challenge. Pulls live listings, scores each property 0–100 from nearby amenities and compares them side by side, with parallel analysis and custom Dify plugins.",
    stack: ["Dify", "OpenAI", "OpenStreetMap", "Lovable"],
  },
  {
    title: "Lidap",
    tag: "Co-founder & CTO",
    link: "https://www.lidap.io/",
    text: "Invoicing SaaS for freelancers. Built the product end to end over two years, shipped it and got the first paying users. Cut invoice creation time from 1 min 30 to 45 s with smart client search and saved services.",
    stack: ["React", "TypeScript", "Firebase", "Tailwind"],
  },
  {
    title: "Forma Host",
    tag: "SaaS in production",
    link: "https://github.com/Firfal/forma-host",
    text: "Online course platform: instructor and student spaces, Vimeo video, Stripe Connect payments, promo codes, chat and push notifications. Every feature tested end to end on emulators; deployed by GitHub Actions with keyless auth.",
    stack: ["Next.js", "Firebase", "Cloud Functions", "Stripe Connect", "GitHub Actions"],
  },
  {
    title: "Max Jeune trip planner",
    tag: "Live",
    link: "https://enhanced-max-planner.web.app",
    text: "Finds every destination reachable for free with the SNCF Max Jeune pass: chains connections, checks the return trip and searches over a date range. Built on SNCF Open Data, with nightly ingestion and golden tests on the routing engine.",
    stack: ["TypeScript", "Next.js", "Cloud Run", "Firestore"],
  },
];

const skills = [
  { title: "AI & agents", items: ["Claude Code", "MCP servers", "OpenAI API", "Dify", "n8n"] },
  { title: "Languages", items: ["TypeScript", "JavaScript", "C / C++", "Python"] },
  { title: "Frontend", items: ["React", "Next.js", "Tailwind CSS"] },
  { title: "Backend & infra", items: ["Firebase", "Cloud Functions", "Stripe", "GitHub Actions"] },
];

const events = [
  "Lovable × Photoroom Buildathon",
  "MCP Connect with OpenAI @leboncoin",
  "Hack the Gap × BLACKBOX.AI",
  "n8n Community Meetup Paris",
  "Paris Claude Founder House",
  "Miro Hackathon",
];

function Section({ title, children }) {
  return (
    <section className="flex flex-col gap-6">
      <h2 className="font-cabinet font-black text-secondary text-2xl md:text-3xl">{title}</h2>
      {children}
    </section>
  );
}

export default function CV() {
  return (
    <div lang="en" className="flex flex-col items-center gap-16 bg-[#F7F4F0] w-full max-w-[100vw] print:bg-white">
      <nav className="flex justify-between items-center pt-8 px-4 max-w-4xl w-full print:hidden">
        <Link to="/" className="font-cabinet text-secondary font-black text-xl md:text-base">
          Quentin Robert
        </Link>
        <a href="mailto:hello@quentinrobert.com" className="bg-yellow text-secondary py-2 px-4 rounded-full text-sm font-semibold">
          Get in touch
        </a>
      </nav>

      <main className="print:hidden flex flex-col gap-16 w-full max-w-4xl px-4 pb-24 text-secondary">
        <header className="flex flex-col gap-6 pt-8">
          <span className="self-start bg-yellow text-secondary py-1 px-3 rounded-full text-sm font-semibold">
            Available now
          </span>
          <div className="flex flex-col gap-2">
            <h1 className="font-cabinet font-black text-5xl md:text-6xl">Quentin Robert</h1>
            <p className="font-cabinet font-bold text-2xl md:text-3xl">AI Engineer</p>
          </div>
          <p className="text-lg font-medium max-w-2xl">
            I build AI agents, MCP servers and the products around them, from the first prototype to production. Student at 42 Paris, co-founder of Lidap, 3rd at the screenpipe hackathon and winner of the Dify challenge at {"{Tech: Europe}"} Paris AI Hack.
          </p>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 max-w-2xl text-sm">
            {facts.map((f) => (
              <div key={f.label} className="flex flex-col">
                <dt className="uppercase text-xs font-semibold opacity-70">{f.label}</dt>
                <dd className="font-medium">{f.value}</dd>
              </div>
            ))}
          </dl>
          <div className="flex flex-wrap gap-3 text-sm">
            {links.map((l) => (
              <a key={l.href} href={l.href} target="_blank" rel="noreferrer" className="border border-secondary rounded-full py-2 px-4 hover:bg-secondary hover:text-main transition-colors">
                {l.label}
              </a>
            ))}
            <a href="/quentin-robert-cv.pdf" download="Quentin-Robert-CV.pdf" className="bg-secondary text-main py-2 px-4 rounded-full font-semibold print:hidden">
              Download PDF ↓
            </a>
          </div>
        </header>

        <Section title="Selected work">
          <div className="grid md:grid-cols-2 gap-4">
            {projects.map((p) => (
              <a
                key={p.title}
                href={p.link}
                target="_blank"
                rel="noreferrer"
                className="flex flex-col gap-3 bg-tertiary rounded-3xl p-6 hover:-translate-y-1 transition-transform break-inside-avoid"
              >
                <div className="flex justify-between items-baseline gap-4">
                  <h3 className="font-cabinet font-black text-xl">{p.title}</h3>
                  <span className="text-xs font-semibold uppercase opacity-70 text-right">{p.tag}</span>
                </div>
                <p className="text-sm leading-relaxed">{p.text}</p>
                <ul className="flex flex-wrap gap-1.5 mt-auto pt-2">
                  {p.stack.map((s) => (
                    <li key={s} className="bg-main rounded-full px-2.5 py-0.5 text-xs font-medium">{s}</li>
                  ))}
                </ul>
              </a>
            ))}
          </div>
        </Section>

        <Section title="Skills">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {skills.map((s) => (
              <div key={s.title} className="flex flex-col gap-2">
                <h3 className="font-semibold text-xs uppercase opacity-70">{s.title}</h3>
                <ul className="flex flex-col gap-1">
                  {s.items.map((i) => <li key={i}>{i}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Experience">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-6 border-b border-secondary/20 pb-4 break-inside-avoid">
              <h3 className="font-cabinet font-black text-lg md:w-56 shrink-0">Lidap</h3>
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-sm">Co-founder & CTO · 2022 – Present</span>
                <p className="text-sm">Designed, built and shipped the whole invoicing app; now serving paying customers.</p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-6 border-b border-secondary/20 pb-4 break-inside-avoid">
              <h3 className="font-cabinet font-black text-lg md:w-56 shrink-0">Freelance</h3>
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-sm">Developer: automations, internal tools, websites · 2023 – Present</span>
                <p className="text-sm">Internal SaaS for agencies, a WhatsApp Business campaign manager (Cloud API), Webflow-to-Firebase migrations and custom websites for clients such as Ecole Motion, Lovis, Pin-Ads and Veiga Motion.</p>
              </div>
            </div>
          </div>
        </Section>

        <Section title="Education">
          <div className="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-6 break-inside-avoid">
            <h3 className="font-cabinet font-black text-lg md:w-56 shrink-0">42 Paris</h3>
            <div className="flex flex-col gap-1">
              <span className="font-semibold text-sm">Software engineering · Nov. 2025 – Present, common core in progress</span>
              <p className="text-sm">Project-based, peer-to-peer curriculum in C and C++: libft, ft_printf, get_next_line, minishell, C++ modules.</p>
            </div>
          </div>
        </Section>

        <Section title="Community">
          <p className="max-w-2xl">
            I spend most weeks in the Paris AI builder scene: hackathons, buildathons and meetups. A few recent ones:
          </p>
          <ul className="flex flex-wrap gap-2">
            {events.map((e) => (
              <li key={e} className="border border-secondary/30 rounded-full py-1.5 px-3 text-sm">{e}</li>
            ))}
          </ul>
        </Section>
      </main>

      <PrintResume />

      <div className="w-full flex justify-center print:hidden">
        <Footer />
      </div>
    </div>
  );
}
